/* eslint-disable prefer-promise-reject-errors,class-methods-use-this */
// noinspection TypeScriptValidateTypes
import { Component, RefObject, createContext, createRef } from 'react';
import { FullScreenProps, FullScreenPropsAndChildren, FullScreenState } from './types';

const FullScreenContext = createContext({
  isFullScreen: false,
  toggleFullscreen: () => null,
});

class FullScreenStateProvider extends Component<FullScreenProps, FullScreenState> {
  targetRef: RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.targetRef = createRef();
    this.state = {
      isFullScreen: false,
      // eslint-disable-next-line react/no-unused-state
      toggleFullscreen: this.toggleFullscreen.bind(this),
    };
  }

  componentDidMount() {
    const targetElem = this.getTargetElement();
    targetElem.onfullscreenchange = (event) => {
      const isTargetElem = targetElem === event.target;
      if (!isTargetElem) {
        // Need to sync state when fullscreen mode turned off but isFullScreen is true
        // Case when more than one fullscreen mode is active and user closed mode by ESC tab
        if (this.state.isFullScreen === true && !document.fullscreenElement) {
          this.setState({ isFullScreen: false });
        }
        return;
      }

      const isFullScreen = document.fullscreenElement === event.target;
      this.setState({ isFullScreen });
    };
    // eslint-disable-next-line react/destructuring-assignment
    if (this.props.startFullscreen) {
      this.toggleFullscreen();
    }
  }

  componentWillUnmount() {
    const targetElem = this.getTargetElement();
    targetElem.onfullscreenchange = null;
  }

  toggleFullscreen() {
    if (!this.state.isFullScreen) {
      this.openFullscreen()?.catch(console.error);
    } else {
      this.exitFullscreen()?.catch(console.error);
    }
    // noinspection TypeScriptValidateTypes
    this.setState({ isFullScreen: !this.state.isFullScreen });
  }

  exitFullscreen(): Promise<any> {
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    }
    if (document.msExitFullscreen) {
      return document.msExitFullscreen();
    }
    return Promise.reject('API does not response');
  }

  openFullscreen(): Promise<any> {
    const elem = this.getTargetElement();
    if (elem.requestFullscreen) {
      return elem.requestFullscreen();
    }
    if (elem.webkitRequestFullscreen) {
      return elem.webkitRequestFullscreen();
    }
    if (elem.msRequestFullscreen) {
      return elem.msRequestFullscreen();
    }
    return Promise.reject('API does not response');
  }

  getTargetElement() {
    const { rootBody } = this.props;
    return rootBody ? window.document.body : this.targetRef.current;
  }

  render() {
    const { children, attrs } = this.props;
    const attr = { id: attrs?.id, className: attrs?.class };
    const customStyles = attrs?.styles || {};
    const styles = {
      height: '100%',
      width: '100%',
      background: '#fff',
      ...customStyles,
    };
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <FullScreenContext.Provider value={this.state}>
        <div ref={this.targetRef} {...attr} style={styles}>
          {children}
        </div>
      </FullScreenContext.Provider>
    );
  }
}

export default function FullScreen({ children, ...rest }: FullScreenPropsAndChildren) {
  return (
    <FullScreenStateProvider {...rest}>
      <FullScreenContext.Consumer>{children}</FullScreenContext.Consumer>
    </FullScreenStateProvider>
  );
}
